/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Bubble, Icon, Text } from 'tea-component';
import { getIsErrorValue } from '../../utils/utils';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getLocale, useSelector } from 'umi';
import cx from 'classnames';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';

interface IMetricsCardDod {
  // 类名
  className?: string;
  // 样式
  style?: React.CSSProperties;

  data: IMetricsDod[];
}

export interface IMetricsDod {
  // 中文标题
  titleEn: string;
  // 英文标题
  titleCn: string;
  // 数据
  value: string | number;
  // 当前数据
  thisValue: string | number;
  // 之前数据
  lastValue: string | number;
  // 当前时间
  thisTime: string;
  // 之前时间
  lastTime: string;
  // 数据格式
  formatRule?: string;
  // 当前&之前数据格式
  extendFormatRule?: string;
  // 英文副标题
  subTitleEn?: string;
  // 中文副标题
  subTitleCn?: string;
  // 隐藏对应的bubble内容
  hideBubbleContent?: boolean;
  // 隐藏上下箭头
  hideDodIcon?: boolean;
  // className
  className?: string;
  // 样式处理
  style?: React.CSSProperties;
  // 是否显示加号
  showPlusIcon?: boolean;
}
interface IFormatMetricsDod {
  // 标题
  title: string;
  // 当前数据
  thisValue: string | number;
  // 之前数据
  lastValue: string | number;
  // 当前时间
  thisTime: string;
  // 之前时间
  lastTime: string;
  // 副标题
  subTitle?: string;
}

export default function  MetricsCardDod(props: IMetricsCardDod) {
  const { data, className, style } = props;
  const  explainInfo  = useSelector((state: any) => state.dashboard?.explainInfo);
  const isEn = getLocale() === 'en-US';

  const itemCont = (data: IFormatMetricsDod | null, ind: number) => {
    if (!data) return null;
    const { title, subTitle, thisTime, lastTime, thisValue, lastValue } = data || {};

    return (
      <div className='dbd-metric-card-dod-bubble-item' key={`${ind}_itemCont`}>
        <div className='dbd-metric-card-dod-bubble-title'>{title} <Text className='dbd-metric-card-dod-bubble-desc'>{subTitle}</Text></div>
        <div className='dbd-metric-card-dod-bubble-flex'>
          <div className='dbd-metric-card-dod-bubble-text'>
            <div className='dbd-metric-card-dod-bubble-t'>{thisTime || '-'}</div>
            <div className='dbd-metric-card-dod-bubble-b'>{lastTime || '-'}</div>
          </div>
          <div className='dbd-metric-card-dod-bubble-ct'>VS</div>
          <div className='dbd-metric-card-dod-bubble-text'>
            <div className='dbd-metric-card-dod-bubble-t'>{thisValue|| '-'}</div>
            <div className='dbd-metric-card-dod-bubble-b'>{lastValue|| '-'}</div>
          </div>
        </div>
      </div>
    );
  };

  // 卡片下方显示同比和环比涨跌的组件
  const UpsAndDownsComparison = () => (
    <div className="overlay-card-body-flex" style={style}>
      <div className='overlay-card-body-flex-item'>
        {
          data?.map((val: { titleEn: string; titleCn: string; }, index : number) => (
            <div key={index} className='overlay-card-body-text'>{ isEn ? val.titleEn : val.titleCn }</div>
          ))
        }
      </div>
      <div className='overlay-card-body-flex-rightitem'>
        {
          data?.map((val: IMetricsDod, index: number) => {
            const { value, formatRule = 'thousandsInit', hideDodIcon, className, style, showPlusIcon } = val;
            const correctVal = !getIsErrorValue(value);
            const isPositive = Number(value) > 0;
            const isZero = Number(value) === 0;

            return (
              <div
                key={index}
                style={{ justifyContent: 'flex-start', ...(style || {}) }}
                className={cx(
                  { 'overlay-card-body-textnum': true },
                  { ['text-green']: correctVal && isPositive },
                  { ['text-red']: correctVal && !isPositive && !isZero },
                  { ['text-gray']: isZero },
                  className,
                )}
              >
                {correctVal ? (
                  <>
                    {showPlusIcon && isPositive && '+'}
                    {dataFormat({ value: Math.abs(Number(value)), formatRule })}
                  </>
                ) : (
                  <span className="data-null"> -</span>
                )}
                {
                  !hideDodIcon && correctVal && (() => {
                    if (isZero) {
                      return <Icon type="trend-zero" />;
                    } if (isPositive) {
                      return <SvgIcon className="overlay-card-body-icon" type="green-up-new" />;
                    } 
                    return <SvgIcon className="overlay-card-body-icon" type="red-down-new" />;
    
                  })()
                }
              </div>
            );
          })
        }
      </div>
    </div>
  );

  // 浮窗提示
  const bubbleContentList = useMemo(() => data?.map((val: IMetricsDod) => {
    const {
      thisValue,
      lastValue,
      extendFormatRule = 'thousandsInit',
      titleEn,
      titleCn,
      subTitleEn,
      subTitleCn,
      thisTime,
      lastTime,
      hideBubbleContent,
    } = val;

    return {
      title: isEn ? titleEn : titleCn,
      // mobile框架指标卡片 变化率卡片英文有描述
      subTitle: isEn ? subTitleEn : subTitleCn,
      thisValue: dataFormat({
        value: thisValue, 
        formatRule: extendFormatRule
      }),
      lastValue: dataFormat({
        value: lastValue, 
        formatRule: extendFormatRule
      }),
      thisTime,
      lastTime,
      hideBubbleContent
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [data, explainInfo, isEn]);

  const bubbleContent = useMemo(() => {
    const contentList = bubbleContentList?.filter(i => !i?.hideBubbleContent);
    const content = contentList?.map((i: IFormatMetricsDod | null, ind: number) =>  itemCont(i, ind));
    return content?.length ? content : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubbleContentList]);

  return (
    <Bubble
      overlayClassName={cx('dbd-metric-card-dod-bubble', className)}
      trigger="hover"
      dark
      placement="bottom-start"
      content={bubbleContent}
    >
      {UpsAndDownsComparison()}
    </Bubble>
  );
};